import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {TuffexProductCatalogueRowStatus, type TuffexProductCatalogueRow} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/tuffexProductCatalogueWorkspaceCapability/domain/TuffexProductCatalogueRow";
import {
    type PostTuffexVariantAttributeType,
    type TuffexVariantAttributeType,
    tuffexVariantAttributeTypeValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/tuffexProductCatalogueWorkspaceCapability/domain/TuffexVariantAttributeType";
import {loadAllPages} from "~/utilities/ottokart/loadAllPages";
import {plutoPost, type PlutoWorkspaceClientContext} from "~/utilities/ottokart/plutoClient";
import {
    buildProductCatalogueIdToInventoryCountMap,
    fetchAllProductInventories,
    upsertProductCatalogueInventory,
} from "~/domainModels/ottokart/inventory";
import {
    buildLookupCatalogueAdditionalColumnValues,
    buildProductCatalogueAdditionalColumnValues,
    buildProductTypeCatalogueAdditionalColumnValues,
    createCatalogueRow,
    deleteCatalogueRow,
    extractCataloguePrice,
    extractCatalogueSku,
    countCatalogueRowsForTemplate,
    fetchCatalogueRowsForTemplate,
    catalogueFiltersWithIds,
    listCatalogueColumnValues,
    namedFiltersFromTextColumns,
    type CatalogueTextColumnFilters,
    fetchSlimCatalogueRowsForTemplate,
    findCatalogueRowBySlug,
    getProductCatalogueRowById,
    catalogueStatusFromIsPublic,
    isActiveCatalogueRow,
    loadTuffexCatalogueTemplates,
    loadTuffexProductTypeCatalogue,
    mapLookupCatalogueRowToDisplayFields,
    readCatalogueTextValue,
    readCatalogueUuidArrayValue,
    readCatalogueUuidValue,
    readCatalogueJsonRecordValue,
    updateCatalogueRow,
    type TuffexLookupColumnMapping,
    type TuffexProductColumnMapping,
    type TuffexProductTypeColumnMapping,
} from "~/domainModels/ottokart/tuffexProductCatalogue";
import {buildOemPartByIdMap, type OemPart, oemPartZodValidator} from "~/domainModels/ottokart/oemParts";
import {buildMachineByIdMap, type Machine, machineZodValidator} from "~/domainModels/ottokart/machines";
import {
    findExistingMediaLibraryFolderId,
    listMediaLibraryFolderFileDirents,
    PRODUCT_BRANDS_MEDIA_LIBRARY_FOLDER_NAME,
    WEBSITE_BRANDS_MEDIA_LIBRARY_FOLDER_NAME,
} from "~/utilities/ottokart/mediaLibraryFolders";
import {
    buildCompatibilityLinksByVariantIdMap,
    syncVariantCompatibilityLinks,
} from "~/domainModels/ottokart/fitment";
import type {ZodType} from "zod";
import {z as zod} from "zod";
import type {ResolvedImage} from "~/components/ottokart/fileInput/fileInputApi";
import {resolveImageIdsFromApi} from "~/components/ottokart/fileInput/fileInputApi";

const PLACEHOLDER_PRODUCT_TYPE_ID = "00000000-0000-4000-8000-000000000002" as Uuid;

const resolvedImageZodValidator = zod.object({
    id: uuidZodValidator,
    imagePath: zod.string(),
    fileName: zod.string().optional(),
    createdAt: zod.string().optional(),
    updatedAt: zod.string().optional(),
});

// --- Types (Get_Product / ProductModel + variantAttributeIds, tags, collectionIds) ---

export type Product = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    slug: string;
    displayName: string;
    description: string;
    type: Uuid;
    categoryId: Uuid | null;
    brandId: Uuid;
    isPublic: boolean;
    status: TuffexProductCatalogueRowStatus;
    imageIds: Uuid[];
    /** Hydrated media-library metadata for `imageIds` (read path). */
    images: ResolvedImage[];
    variantAttributeIds: Uuid[];
    tagIds: Uuid[];
    collectionIds: Uuid[];
};

// --- ProductCategoryModel / Product Categories ---

export type ProductCategory = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    displayName: string;
    slug: string;
    sortOrder: number;
    imageId: Uuid | null;
    parentId: Uuid | null;
    metaTitle: string | null;
    metaDescription: string | null;
};

export type PostProductCategory = {
    id?: Uuid;
    createdAt?: Iso8601DateTime;
    updatedAt?: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy?: Uuid;
    displayName: string;
    slug: string;
    sortOrder: number;
    imageId?: Uuid | null;
    parentId?: Uuid | null;
    metaTitle?: string | null;
    metaDescription?: string | null;
};

// --- ProductTypeModel / Product Types ---

export type ProductType = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    displayName: string;
    slug: string;
    sortOrder: number;
    categoryId: Uuid | null;
};

export type PostProductType = {
    id?: Uuid;
    createdAt?: Iso8601DateTime;
    updatedAt?: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    displayName: string;
    slug: string;
    sortOrder: number;
    categoryId?: Uuid | null;
};

// --- ProductBrandModel / Product Brands ---

export type ProductBrand = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    displayName: string;
    slug: string;
    sortOrder: number;
    imageId: Uuid | null;
};

export type PostProductBrand = {
    id?: Uuid;
    createdAt?: Iso8601DateTime;
    updatedAt?: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    displayName: string;
    slug: string;
    sortOrder: number;
    imageId?: Uuid | null;
};
// --- VariantAttributeTypeModel / Variant Attribute Types ---

export type VariantAttributeType = TuffexVariantAttributeType;
export type PostVariantAttributeType = PostTuffexVariantAttributeType;

// --- ListProductsRequest ---

export type ListProductsRequest = {
    page?: number;
    size?: number;
    sort?: string;
    type?: Uuid;
    brandId?: Uuid;
    collection?: Uuid;
    categoryId?: Uuid;
    search?: string;
};

// --- PostProduct (Post_Product: create/update body; id required for update) ---

export type PostProduct = {
    id?: Uuid;
    createdAt?: Iso8601DateTime;
    updatedAt?: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    slug: string;
    displayName: string;
    description: string;
    type: Uuid;
    categoryId?: Uuid | null;
    brandId: Uuid;
    isPublic: boolean;
    isDeleted: boolean;
    imageIds: Uuid[];
    variantAttributeIds: Uuid[];
    tagIds: Uuid[];
    collectionIds: Uuid[];
};

// --- Variant (Get_Variant = VariantModel + inventory, compatibleMachines, equivalentOemParts) ---

export type Variant = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    productId: Uuid;
    sku: string;
    slug: string;
    displayName: string;
    description: string;
    sellingPrice: number;
    mrp: number;
    hsnCode: string;
    isInclusiveAllTaxes: boolean;
    isPublic: boolean;
    attributeValues: Record<string, unknown>;
    additionalDetails: Record<string, unknown>;
    imageIds: Uuid[];
    /** Hydrated media-library metadata for `imageIds` (read path). */
    images: ResolvedImage[];
    inventory: number;
    compatibleMachines: Machine[];
    equivalentOemParts: OemPart[];
    tagIds: Uuid[];
};

// --- PostVariant (Post_Variant: create/update variant body; id required for update) ---

export type PostVariant = {
    id?: Uuid;
    createdAt?: Iso8601DateTime;
    updatedAt?: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    productId: Uuid;
    sku: string;
    slug: string;
    displayName: string;
    description: string;
    sellingPrice: number;
    mrp: number;
    hsnCode: string;
    isInclusiveAllTaxes: boolean;
    isPublic: boolean;
    isDeleted: boolean;
    attributeValues: Record<string, unknown>;
    additionalDetails: Record<string, unknown>;
    imageIds: Uuid[];
    inventory: number;
    compatibleMachineIds: Uuid[];
    equivalentOemPartIds: Uuid[];
    tagIds: Uuid[];
};

// --- ProductWithVariants (Get_ProductWithVariants) ---

export type ProductWithVariants = Product & {
    variants: Variant[];
};

// --- Zod validators (core data only; robustJsonResultClientFetch unwraps success/data) ---

export const variantZodValidator: ZodType<Variant> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    productId: uuidZodValidator,
    sku: zod.string(),
    slug: zod.string(),
    displayName: zod.string(),
    description: zod.string(),
    sellingPrice: zod.number(),
    mrp: zod.number(),
    hsnCode: zod.string(),
    isInclusiveAllTaxes: zod.boolean(),
    isPublic: zod.boolean(),
    attributeValues: zod.record(zod.string(), zod.unknown()),
    additionalDetails: zod.record(zod.string(), zod.unknown()),
    imageIds: zod.array(uuidZodValidator),
    images: zod.array(resolvedImageZodValidator).default([]),
    inventory: zod.number(),
    compatibleMachines: zod.array(machineZodValidator),
    equivalentOemParts: zod.array(oemPartZodValidator),
    tagIds: zod.array(uuidZodValidator),
});

export const postVariantZodValidator: ZodType<PostVariant> = zod.object({
    id: uuidZodValidator.optional(),
    createdAt: iso8601DateTimeZodValidator.optional(),
    updatedAt: iso8601DateTimeZodValidator.optional(),
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    productId: uuidZodValidator,
    sku: zod.string(),
    slug: zod.string(),
    displayName: zod.string(),
    description: zod.string(),
    sellingPrice: zod.number(),
    mrp: zod.number(),
    hsnCode: zod.string(),
    isInclusiveAllTaxes: zod.boolean(),
    isPublic: zod.boolean(),
    isDeleted: zod.literal(false),
    attributeValues: zod.record(zod.string(), zod.unknown()),
    additionalDetails: zod.record(zod.string(), zod.unknown()),
    imageIds: zod.array(uuidZodValidator),
    inventory: zod.number(),
    compatibleMachineIds: zod.array(uuidZodValidator),
    equivalentOemPartIds: zod.array(uuidZodValidator),
    tagIds: zod.array(uuidZodValidator),
});

export const productZodValidator: ZodType<Product> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    slug: zod.string(),
    displayName: zod.string(),
    description: zod.string(),
    type: uuidZodValidator,
    categoryId: uuidZodValidator.nullable(),
    brandId: uuidZodValidator,
    isPublic: zod.boolean(),
    status: zod.enum(TuffexProductCatalogueRowStatus),
    imageIds: zod.array(uuidZodValidator),
    images: zod.array(resolvedImageZodValidator).default([]),
    variantAttributeIds: zod.array(uuidZodValidator),
    tagIds: zod.array(uuidZodValidator),
    collectionIds: zod.array(uuidZodValidator),
});

export const productCategoryZodValidator: ZodType<ProductCategory> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    displayName: zod.string(),
    slug: zod.string(),
    sortOrder: zod.number().int().nonnegative(),
    imageId: uuidZodValidator.nullable(),
    parentId: uuidZodValidator.nullable(),
    metaTitle: zod.string().nullable().optional().transform((value) => value ?? null),
    metaDescription: zod.string().nullable().optional().transform((value) => value ?? null),
});

export const postProductCategoryZodValidator: ZodType<PostProductCategory> = zod.object({
    id: uuidZodValidator.optional(),
    createdAt: iso8601DateTimeZodValidator.optional(),
    updatedAt: iso8601DateTimeZodValidator.optional(),
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator.optional(),
    displayName: zod.string(),
    slug: zod.string(),
    sortOrder: zod.number().int().nonnegative(),
    imageId: uuidZodValidator.nullable().optional(),
    parentId: uuidZodValidator.nullable().optional(),
    metaTitle: zod.string().nullable().optional(),
    metaDescription: zod.string().nullable().optional(),
});

export const productTypeZodValidator: ZodType<ProductType> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    displayName: zod.string(),
    slug: zod.string(),
    sortOrder: zod.number().int().nonnegative(),
    categoryId: uuidZodValidator.nullable().optional().transform((value) => value ?? null),
});

export const postProductTypeZodValidator: ZodType<PostProductType> = zod.object({
    id: uuidZodValidator.optional(),
    createdAt: iso8601DateTimeZodValidator.optional(),
    updatedAt: iso8601DateTimeZodValidator.optional(),
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    displayName: zod.string(),
    slug: zod.string(),
    sortOrder: zod.number().int().nonnegative(),
    categoryId: uuidZodValidator.nullable().optional(),
});

export const productBrandZodValidator: ZodType<ProductBrand> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    displayName: zod.string(),
    slug: zod.string(),
    sortOrder: zod.number().int().nonnegative(),
    imageId: uuidZodValidator.nullable(),
});

export const postProductBrandZodValidator: ZodType<PostProductBrand> = zod.object({
    id: uuidZodValidator.optional(),
    createdAt: iso8601DateTimeZodValidator.optional(),
    updatedAt: iso8601DateTimeZodValidator.optional(),
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    displayName: zod.string(),
    slug: zod.string(),
    sortOrder: zod.number().int().nonnegative(),
    imageId: uuidZodValidator.nullable().optional(),
});

export const postProductZodValidator: ZodType<PostProduct> = zod.object({
    id: uuidZodValidator.optional(),
    createdAt: iso8601DateTimeZodValidator.optional(),
    updatedAt: iso8601DateTimeZodValidator.optional(),
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    slug: zod.string(),
    displayName: zod.string(),
    description: zod.string(),
    type: uuidZodValidator,
    categoryId: uuidZodValidator.nullable().optional(),
    brandId: uuidZodValidator,
    isPublic: zod.boolean(),
    isDeleted: zod.literal(false),
    imageIds: zod.array(uuidZodValidator),
    variantAttributeIds: zod.array(uuidZodValidator),
    tagIds: zod.array(uuidZodValidator),
    collectionIds: zod.array(uuidZodValidator),
});

export const listProductsRequestZodValidator: ZodType<ListProductsRequest> = zod.object({
    page: zod.number().optional(),
    size: zod.number().optional(),
    sort: zod.string().optional(),
    type: uuidZodValidator.optional(),
    brandId: uuidZodValidator.optional(),
    collection: uuidZodValidator.optional(),
    categoryId: uuidZodValidator.optional(),
    search: zod.string().optional(),
});

export const productWithVariantsZodValidator: ZodType<ProductWithVariants> = productZodValidator.and(
    zod.object({
        variants: zod.array(variantZodValidator),
    }),
);

// --- API calls ---

async function loadTuffexProductCatalogueContext(
    context: PlutoWorkspaceClientContext,
): Promise<
    Result<{
        productColumnMapping: TuffexProductColumnMapping;
        productTemplateId: Uuid;
    }>
> {
    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    return dataResult({
        productColumnMapping: templatesResult.data.productColumns,
        productTemplateId: templatesResult.data.productColumns.templateId,
    });
}

async function fetchTuffexProductCatalogues(
    context: PlutoWorkspaceClientContext & {
        limit?: number;
        namedFilters?: ReturnType<typeof namedFiltersFromTextColumns>;
        offset?: number;
        parentsOnly?: boolean;
        search?: string;
        searchColumnIds?: Array<Uuid>;
    },
): Promise<Result<Array<TuffexProductCatalogueRow>>> {
    const productContextResult = await loadTuffexProductCatalogueContext(context);
    if (productContextResult.success !== true) {
        return productContextResult;
    }

    return fetchCatalogueRowsForTemplate({
        ...context,
        includeArchived: true,
        productCatalogueTemplateId: productContextResult.data.productTemplateId,
    });
}

/**
 * Fetches a single catalogue row by id. Prefer this over {@link fetchTuffexProductCatalogues}
 * for update/delete paths — re-listing the full catalogue per mutation is extremely slow.
 */
async function fetchTuffexProductCatalogueById(
    context: PlutoWorkspaceClientContext & {
        productCatalogueId: string;
    },
): Promise<Result<TuffexProductCatalogueRow>> {
    const parsedProductCatalogueId = uuidZodValidator.safeParse(context.productCatalogueId);
    if (parsedProductCatalogueId.success !== true) {
        return errorResult(new Error(`Invalid product catalogue id: ${context.productCatalogueId}`));
    }

    const productContextResult = await loadTuffexProductCatalogueContext(context);
    if (productContextResult.success !== true) {
        return productContextResult;
    }

    return getProductCatalogueRowById({
        accessToken: context.accessToken,
        intellsysBaseUrl: context.intellsysBaseUrl,
        productCatalogueId: parsedProductCatalogueId.data,
        productCatalogueTemplateId: productContextResult.data.productTemplateId,
        workspaceId: context.workspaceId,
    });
}

function resolveCatalogueImageIds({
    columnMapping,
    plutoProductCatalogue,
}: {
    columnMapping: TuffexProductColumnMapping;
    plutoProductCatalogue: TuffexProductCatalogueRow;
}): Array<Uuid> {
    return readCatalogueUuidArrayValue(
        plutoProductCatalogue.additionalColumnValues,
        columnMapping.imageIdsColumnId,
    );
}

function mapPlutoProductCatalogueToProduct(
    plutoProductCatalogue: TuffexProductCatalogueRow,
    columnMapping: TuffexProductColumnMapping,
): Product {
    const sku = extractCatalogueSku(plutoProductCatalogue, columnMapping);
    const displayNameFromColumn = readCatalogueTextValue(
        plutoProductCatalogue.additionalColumnValues,
        columnMapping.displayNameColumnId,
    );
    const slugFromColumn = readCatalogueTextValue(plutoProductCatalogue.additionalColumnValues, columnMapping.slugColumnId);
    const displayName =
        displayNameFromColumn.trim().length > 0
            ? displayNameFromColumn.trim()
            : sku.length > 0
              ? sku
              : plutoProductCatalogue.id;
    const slug = slugFromColumn.trim().length > 0 ? slugFromColumn.trim() : displayName;
    const description = readCatalogueTextValue(plutoProductCatalogue.additionalColumnValues, columnMapping.descriptionColumnId);
    const categoryId = readCatalogueUuidValue(plutoProductCatalogue.additionalColumnValues, columnMapping.categoryIdColumnId);
    const brandId = readCatalogueUuidValue(plutoProductCatalogue.additionalColumnValues, columnMapping.brandIdColumnId);
    const productTypeId = readCatalogueUuidValue(
        plutoProductCatalogue.additionalColumnValues,
        columnMapping.productTypeIdColumnId,
    );

    return {
        id: plutoProductCatalogue.id,
        createdAt: plutoProductCatalogue.createdAt,
        updatedAt: plutoProductCatalogue.updatedAt,
        createdBy: plutoProductCatalogue.createdBy,
        updatedBy: plutoProductCatalogue.updatedBy,
        slug: slug,
        displayName: displayName,
        description: description,
        type: productTypeId ?? PLACEHOLDER_PRODUCT_TYPE_ID,
        categoryId: categoryId,
        brandId: brandId ?? PLACEHOLDER_PRODUCT_TYPE_ID,
        isPublic: isActiveCatalogueRow(plutoProductCatalogue),
        status: plutoProductCatalogue.status,
        imageIds: resolveCatalogueImageIds({
            columnMapping: columnMapping,
            plutoProductCatalogue: plutoProductCatalogue,
        }),
        images: [],
        variantAttributeIds: readCatalogueUuidArrayValue(
            plutoProductCatalogue.additionalColumnValues,
            columnMapping.variantAttributeIdsColumnId,
        ),
        tagIds: [],
        collectionIds: [],
    };
}

function resolveEquivalentOemParts({
    columnMapping,
    oemPartById,
    plutoProductCatalogue,
}: {
    columnMapping: TuffexProductColumnMapping;
    oemPartById: Map<Uuid, OemPart>;
    plutoProductCatalogue: TuffexProductCatalogueRow;
}): Array<OemPart> {
    const equivalentOemPartIds = readCatalogueUuidArrayValue(
        plutoProductCatalogue.additionalColumnValues,
        columnMapping.equivalentOemPartIdsColumnId,
    );

    const equivalentOemParts: Array<OemPart> = [];
    for (const oemPartId of equivalentOemPartIds) {
        const oemPart = oemPartById.get(oemPartId);
        if (oemPart !== undefined) {
            equivalentOemParts.push(oemPart);
        }
    }

    return equivalentOemParts;
}

function resolveCompatibleMachines({
    compatibilityLinksByVariantId,
    machineById,
    variantCatalogueId,
}: {
    compatibilityLinksByVariantId?: Map<Uuid, Array<Uuid>>;
    machineById: Map<Uuid, Machine>;
    variantCatalogueId: Uuid;
}): Array<Machine> {
    const masterNodeIds = compatibilityLinksByVariantId?.get(variantCatalogueId) ?? [];
    const compatibleMachines: Array<Machine> = [];

    for (const masterNodeId of masterNodeIds) {
        const machine = machineById.get(masterNodeId);
        if (machine !== undefined) {
            compatibleMachines.push(machine);
        }
    }

    return compatibleMachines;
}

function mapPlutoProductCatalogueToVariant(
    plutoProductCatalogue: TuffexProductCatalogueRow,
    columnMapping: TuffexProductColumnMapping,
    inventoryCountByProductCatalogueId?: Map<Uuid, number>,
    oemPartById?: Map<Uuid, OemPart>,
    machineById?: Map<Uuid, Machine>,
    compatibilityLinksByVariantId?: Map<Uuid, Array<Uuid>>,
): Variant {
    const sku = extractCatalogueSku(plutoProductCatalogue, columnMapping);
    const displayNameFromColumn = readCatalogueTextValue(
        plutoProductCatalogue.additionalColumnValues,
        columnMapping.displayNameColumnId,
    );
    const slugFromColumn = readCatalogueTextValue(plutoProductCatalogue.additionalColumnValues, columnMapping.slugColumnId);
    const displayName =
        displayNameFromColumn.trim().length > 0
            ? displayNameFromColumn.trim()
            : sku.length > 0
              ? sku
              : plutoProductCatalogue.id;
    const slug = slugFromColumn.trim().length > 0 ? slugFromColumn.trim() : displayName;
    const sellingPrice = extractCataloguePrice(plutoProductCatalogue, columnMapping);
    const equivalentOemParts =
        oemPartById === undefined
            ? []
            : resolveEquivalentOemParts({
                  columnMapping: columnMapping,
                  oemPartById: oemPartById,
                  plutoProductCatalogue: plutoProductCatalogue,
              });
    const compatibleMachines =
        machineById === undefined
            ? []
            : resolveCompatibleMachines({
                  compatibilityLinksByVariantId: compatibilityLinksByVariantId,
                  machineById: machineById,
                  variantCatalogueId: plutoProductCatalogue.id,
              });

    return {
        id: plutoProductCatalogue.id,
        createdAt: plutoProductCatalogue.createdAt,
        updatedAt: plutoProductCatalogue.updatedAt,
        createdBy: plutoProductCatalogue.createdBy,
        updatedBy: plutoProductCatalogue.updatedBy,
        productId: plutoProductCatalogue.parentId ?? plutoProductCatalogue.id,
        sku: sku,
        slug: slug,
        displayName: displayName,
        description: "",
        sellingPrice: sellingPrice,
        mrp: sellingPrice,
        hsnCode: "",
        isInclusiveAllTaxes: false,
        isPublic: isActiveCatalogueRow(plutoProductCatalogue),
        attributeValues: readCatalogueJsonRecordValue(
            plutoProductCatalogue.additionalColumnValues,
            columnMapping.attributeValuesColumnId,
        ),
        additionalDetails: readCatalogueJsonRecordValue(
            plutoProductCatalogue.additionalColumnValues,
            columnMapping.additionalDetailsColumnId,
        ),
        imageIds: resolveCatalogueImageIds({
            columnMapping: columnMapping,
            plutoProductCatalogue: plutoProductCatalogue,
        }),
        images: [],
        inventory: inventoryCountByProductCatalogueId?.get(plutoProductCatalogue.id) ?? 0,
        compatibleMachines: compatibleMachines,
        equivalentOemParts: equivalentOemParts,
        tagIds: [],
    };
}

function mapPlutoProductCatalogueToProductWithVariants({
    childCatalogues,
    columnMapping,
    compatibilityLinksByVariantId,
    inventoryCountByProductCatalogueId,
    machineById,
    oemPartById,
    plutoProductCatalogue,
}: {
    childCatalogues: Array<TuffexProductCatalogueRow>;
    columnMapping: TuffexProductColumnMapping;
    compatibilityLinksByVariantId?: Map<Uuid, Array<Uuid>>;
    inventoryCountByProductCatalogueId?: Map<Uuid, number>;
    machineById?: Map<Uuid, Machine>;
    oemPartById?: Map<Uuid, OemPart>;
    plutoProductCatalogue: TuffexProductCatalogueRow;
}): ProductWithVariants {
    const variants =
        childCatalogues.length > 0
            ? childCatalogues.map((childCatalogue) => {
                  return mapPlutoProductCatalogueToVariant(
                      childCatalogue,
                      columnMapping,
                      inventoryCountByProductCatalogueId,
                      oemPartById,
                      machineById,
                      compatibilityLinksByVariantId,
                  );
              })
            : [
                  mapPlutoProductCatalogueToVariant(
                      plutoProductCatalogue,
                      columnMapping,
                      inventoryCountByProductCatalogueId,
                      oemPartById,
                      machineById,
                      compatibilityLinksByVariantId,
                  ),
              ];

    return {
        ...mapPlutoProductCatalogueToProduct(plutoProductCatalogue, columnMapping),
        variants: variants,
    };
}

async function loadTuffexProductColumnMapping(
    context: PlutoWorkspaceClientContext,
): Promise<Result<TuffexProductColumnMapping>> {
    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    return dataResult(templatesResult.data.productColumns);
}

function buildProductAdditionalColumnValues({
    additionalDetails,
    attributeValues,
    brandId,
    categoryId,
    columnMapping,
    description,
    displayName,
    equivalentOemPartIds,
    imageIds,
    includeSellableFields = true,
    price,
    productTypeId,
    sku,
    slug,
    variantAttributeIds,
}: {
    additionalDetails?: Record<string, unknown>;
    attributeValues?: Record<string, unknown>;
    brandId: Uuid | null;
    categoryId: Uuid | null;
    columnMapping: TuffexProductColumnMapping;
    description: string;
    displayName: string;
    equivalentOemPartIds?: Array<Uuid>;
    imageIds?: Array<Uuid>;
    includeSellableFields?: boolean;
    price: number;
    productTypeId?: Uuid | null;
    sku: string;
    slug: string;
    variantAttributeIds?: Array<Uuid>;
}): Record<string, unknown> {
    return buildProductCatalogueAdditionalColumnValues({
        additionalDetails: additionalDetails,
        attributeValues: attributeValues,
        brandId: brandId,
        categoryId: categoryId,
        columnMapping: columnMapping,
        description: description,
        displayName: displayName,
        equivalentOemPartIds: equivalentOemPartIds,
        imageIds: imageIds,
        includeSellableFields: includeSellableFields,
        price: price,
        productTypeId: productTypeId,
        sku: sku,
        slug: slug,
        variantAttributeIds: variantAttributeIds,
    });
}

async function updateTuffexProductCatalogueRow({
    additionalDetails,
    attributeValues,
    columnMapping,
    description,
    displayName,
    equivalentOemPartIds,
    imageIds,
    parentId,
    plutoProductCatalogue,
    sellingPrice,
    sku,
    slug,
    brandId,
    categoryId,
    isPublic,
    productTypeId,
    variantAttributeIds,
    ...context
}: PlutoWorkspaceClientContext & {
    additionalDetails?: Record<string, unknown>;
    attributeValues?: Record<string, unknown>;
    brandId: Uuid | null;
    categoryId: Uuid | null;
    columnMapping: TuffexProductColumnMapping;
    description: string;
    displayName: string;
    equivalentOemPartIds?: Array<Uuid>;
    imageIds?: Array<Uuid>;
    isPublic?: boolean;
    parentId: Uuid | null;
    plutoProductCatalogue: TuffexProductCatalogueRow;
    productTypeId?: Uuid | null;
    sellingPrice: number;
    sku: string;
    slug: string;
    variantAttributeIds?: Array<Uuid>;
}): Promise<Result<void>> {
    return updateCatalogueRow({
        ...context,
        additionalColumnValues: buildProductAdditionalColumnValues({
            additionalDetails: additionalDetails,
            attributeValues: attributeValues,
            brandId: brandId,
            categoryId: categoryId,
            columnMapping: columnMapping,
            description: description,
            displayName: displayName,
            equivalentOemPartIds: equivalentOemPartIds,
            imageIds: imageIds,
            price: sellingPrice,
            productTypeId: productTypeId,
            sku: sku,
            slug: slug,
            variantAttributeIds: variantAttributeIds,
        }),
        catalogueRow: plutoProductCatalogue,
        parentId: parentId,
        ...(isPublic !== undefined
            ? {status: catalogueStatusForVisibilityUpdate(isPublic, plutoProductCatalogue.status)}
            : {}),
    });
}

function catalogueStatusForVisibilityUpdate(
    isPublic: boolean,
    currentStatus: TuffexProductCatalogueRowStatus,
): TuffexProductCatalogueRowStatus {
    if (isPublic === true) {
        return TuffexProductCatalogueRowStatus.active;
    }
    if (currentStatus === TuffexProductCatalogueRowStatus.underReview) {
        return TuffexProductCatalogueRowStatus.underReview;
    }
    return TuffexProductCatalogueRowStatus.archived;
}

export async function setProductListingStatus({
    catalogueIds,
    status,
    ...context
}: PlutoWorkspaceClientContext & {
    catalogueIds: Array<Uuid>;
    status: TuffexProductCatalogueRowStatus;
}): Promise<Result<void>> {
    for (const catalogueId of catalogueIds) {
        const rowResult = await fetchTuffexProductCatalogueById({
            ...context,
            productCatalogueId: catalogueId,
        });
        if (rowResult.success !== true) {
            return rowResult;
        }
        const catalogueRow = rowResult.data;
        const updateResult = await updateCatalogueRow({
            ...context,
            additionalColumnValues: catalogueRow.additionalColumnValues,
            catalogueRow: catalogueRow,
            parentId: catalogueRow.parentId,
            status: status,
        });
        if (updateResult.success !== true) {
            return updateResult;
        }
    }
    return dataResult(undefined);
}

async function findTuffexProductCatalogueBySku({
    catalogues,
    columnMapping,
    parentId,
    sku,
}: {
    catalogues: Array<TuffexProductCatalogueRow>;
    columnMapping: TuffexProductColumnMapping;
    parentId?: Uuid | null;
    sku: string;
}): Promise<TuffexProductCatalogueRow | null> {
    const normalizedSku = sku.trim().toLowerCase();
    if (normalizedSku.length === 0) {
        return null;
    }

    for (const catalogue of catalogues) {
        if (parentId !== undefined) {
            if (parentId === null) {
                if (catalogue.parentId !== null) {
                    continue;
                }
            } else if (catalogue.parentId !== parentId) {
                continue;
            }
        }

        const catalogueSku = extractCatalogueSku(catalogue, columnMapping);
        if (catalogueSku.trim().toLowerCase() === normalizedSku) {
            return catalogue;
        }
    }
    return null;
}

async function loadInventoryCountByProductCatalogueId(
    context: PlutoWorkspaceClientContext,
): Promise<Result<Map<Uuid, number>>> {
    const productInventoriesResult = await fetchAllProductInventories(context);
    if (productInventoriesResult.success !== true) {
        return productInventoriesResult;
    }

    return dataResult(buildProductCatalogueIdToInventoryCountMap(productInventoriesResult.data));
}

export async function mapOttokartCatalogueRowsToAdminProducts(
    context: PlutoWorkspaceClientContext & {
        catalogueRows: Array<TuffexProductCatalogueRow>;
    },
): Promise<Result<Array<Product>>> {
    const columnMappingResult = await loadTuffexProductColumnMapping(context);
    if (columnMappingResult.success !== true) {
        return columnMappingResult;
    }

    const products = context.catalogueRows.map((catalogueRow) => {
        return mapPlutoProductCatalogueToProduct(catalogueRow, columnMappingResult.data);
    });
    return dataResult(products);
}

export async function buildProductCatalogueSkuMaps(
    context: PlutoWorkspaceClientContext,
): Promise<Result<{
    productCatalogueIdByNormalizedSku: Map<string, Uuid>;
    skuByProductCatalogueId: Map<Uuid, string>;
}>> {
    const columnMappingResult = await loadTuffexProductColumnMapping(context);
    if (columnMappingResult.success !== true) {
        return columnMappingResult;
    }

    const cataloguesResult = await fetchTuffexProductCatalogues(context);
    if (cataloguesResult.success !== true) {
        return cataloguesResult;
    }

    const skuByProductCatalogueId = new Map<Uuid, string>();
    const productCatalogueIdByNormalizedSku = new Map<string, Uuid>();

    for (const catalogue of cataloguesResult.data) {
        const sku = extractCatalogueSku(catalogue, columnMappingResult.data);
        skuByProductCatalogueId.set(catalogue.id, sku);
        const normalizedSku = sku.trim().toLowerCase();
        if (normalizedSku.length > 0) {
            productCatalogueIdByNormalizedSku.set(normalizedSku, catalogue.id);
        }
    }

    return dataResult({
        productCatalogueIdByNormalizedSku: productCatalogueIdByNormalizedSku,
        skuByProductCatalogueId: skuByProductCatalogueId,
    });
}

export async function getProducts({
    accessToken,
    filters,
    limit,
    listParams: _listParams,
    offset,
    search,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    filters?: CatalogueTextColumnFilters;
    limit?: number;
    listParams?: ListProductsRequest;
    offset?: number;
    search?: string;
}): Promise<Result<Array<Product>>> {
    const columnMappingResult = await loadTuffexProductColumnMapping({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (columnMappingResult.success !== true) {
        return columnMappingResult;
    }

    const isPaged = limit !== undefined && offset !== undefined;
    const fetchPage = (pageOffset: number) => fetchTuffexProductCatalogues({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        namedFilters: namedFiltersFromTextColumns({
            displayNameColumnId: columnMappingResult.data.displayNameColumnId,
            filters: catalogueFiltersWithIds({
                columnIds: [
                    {columnId: columnMappingResult.data.brandIdColumnId, values: filters?.brandIds},
                    {columnId: columnMappingResult.data.categoryIdColumnId, values: filters?.categoryIds},
                    {columnId: columnMappingResult.data.productTypeIdColumnId, values: filters?.productTypeIds},
                ],
                filters: filters,
            }),
            slugColumnId: columnMappingResult.data.slugColumnId,
        }),
        limit: isPaged === true ? limit : 500,
        offset: pageOffset,
        ...(isPaged === true ? {parentsOnly: true} : {}),
        search: search,
        searchColumnIds: [
            columnMappingResult.data.displayNameColumnId,
            columnMappingResult.data.slugColumnId,
            columnMappingResult.data.descriptionColumnId,
            columnMappingResult.data.skuColumnId,
        ].filter((columnId): columnId is Uuid => columnId !== null),
        workspaceId: workspaceId,
    });
    const cataloguesResult = isPaged === true
        ? await fetchPage(offset)
        : await loadAllPages({fetchPage: fetchPage, pageSize: 500});
    if (cataloguesResult.success !== true) {
        return cataloguesResult;
    }
    const productRows = isPaged === true
        ? cataloguesResult.data
        : cataloguesResult.data.filter((row) => row.parentId === null);

    return dataResult(
        productRows.map((plutoProductCatalogue) => {
            return mapPlutoProductCatalogueToProduct(plutoProductCatalogue, columnMappingResult.data);
        }),
    );
}

export async function countProducts(context: PlutoWorkspaceClientContext & {filters?: CatalogueTextColumnFilters; search?: string}): Promise<Result<number>> {
    const columnMappingResult = await loadTuffexProductColumnMapping(context);
    if (columnMappingResult.success !== true) {
        return columnMappingResult;
    }
    return countCatalogueRowsForTemplate({
        ...context,
        includeArchived: true,
        namedFilters: namedFiltersFromTextColumns({
            displayNameColumnId: columnMappingResult.data.displayNameColumnId,
            filters: catalogueFiltersWithIds({
                columnIds: [
                    {columnId: columnMappingResult.data.brandIdColumnId, values: context.filters?.brandIds},
                    {columnId: columnMappingResult.data.categoryIdColumnId, values: context.filters?.categoryIds},
                    {columnId: columnMappingResult.data.productTypeIdColumnId, values: context.filters?.productTypeIds},
                ],
                filters: context.filters,
            }),
            slugColumnId: columnMappingResult.data.slugColumnId,
        }),
        parentsOnly: true,
        productCatalogueTemplateId: columnMappingResult.data.templateId,
        search: context.search,
        searchColumnIds: [
            columnMappingResult.data.displayNameColumnId,
            columnMappingResult.data.slugColumnId,
            columnMappingResult.data.descriptionColumnId,
            columnMappingResult.data.skuColumnId,
        ].filter((columnId): columnId is Uuid => columnId !== null),
    });
}

export async function countProductsForKanbanColumn(
    context: PlutoWorkspaceClientContext & {
        column: "active" | "draft" | "underReview";
        filters?: CatalogueTextColumnFilters;
        search?: string;
    },
): Promise<Result<number>> {
    const columnStatus = context.column === "active"
        ? TuffexProductCatalogueRowStatus.active
        : context.column === "underReview"
          ? TuffexProductCatalogueRowStatus.underReview
          : TuffexProductCatalogueRowStatus.archived;
    const selectedStatuses = context.filters?.statusValues;
    if (selectedStatuses !== undefined && selectedStatuses !== null && selectedStatuses.includes(columnStatus) !== true) {
        return dataResult(0);
    }
    return countProducts({
        accessToken: context.accessToken,
        filters: {
            brandIds: context.filters?.brandIds,
            categoryIds: context.filters?.categoryIds,
            createdAt: context.filters?.createdAt,
            displayName: context.filters?.displayName,
            manufacturerIds: context.filters?.manufacturerIds,
            productTypeIds: context.filters?.productTypeIds,
            slug: context.filters?.slug,
            statusValues: [columnStatus],
            updatedAt: context.filters?.updatedAt,
            valueFilters: context.filters?.valueFilters,
        },
        intellsysBaseUrl: context.intellsysBaseUrl,
        search: context.search,
        workspaceId: context.workspaceId,
    });
}

async function listMappedCatalogueColumnValues({
    columnId,
    context,
    includeArchived,
    parentsOnly,
    templateId,
}: {
    columnId: Uuid | null;
    context: PlutoWorkspaceClientContext;
    includeArchived?: boolean;
    parentsOnly?: boolean;
    templateId: Uuid;
}): Promise<Result<Array<string>>> {
    return listCatalogueColumnValues({
        ...context,
        columnId: columnId,
        includeArchived: includeArchived,
        parentsOnly: parentsOnly,
        templateId: templateId,
    });
}

export async function listProductFilterColumnValues(context: PlutoWorkspaceClientContext): Promise<Result<{
    brandId: Array<string>;
    categoryId: Array<string>;
    displayName: Array<string>;
    productTypeId: Array<string>;
}>> {
    const columnMappingResult = await loadTuffexProductColumnMapping(context);
    if (columnMappingResult.success !== true) {
        return columnMappingResult;
    }
    const mapping = columnMappingResult.data;
    const displayNameResult = await listMappedCatalogueColumnValues({
        columnId: mapping.displayNameColumnId,
        context: context,
        includeArchived: true,
        parentsOnly: true,
        templateId: mapping.templateId,
    });
    if (displayNameResult.success !== true) {
        return displayNameResult;
    }
    const brandResult = await listMappedCatalogueColumnValues({
        columnId: mapping.brandIdColumnId,
        context: context,
        includeArchived: true,
        parentsOnly: true,
        templateId: mapping.templateId,
    });
    if (brandResult.success !== true) {
        return brandResult;
    }
    const categoryResult = await listMappedCatalogueColumnValues({
        columnId: mapping.categoryIdColumnId,
        context: context,
        includeArchived: true,
        parentsOnly: true,
        templateId: mapping.templateId,
    });
    if (categoryResult.success !== true) {
        return categoryResult;
    }
    const typeResult = await listMappedCatalogueColumnValues({
        columnId: mapping.productTypeIdColumnId,
        context: context,
        includeArchived: true,
        parentsOnly: true,
        templateId: mapping.templateId,
    });
    if (typeResult.success !== true) {
        return typeResult;
    }
    return dataResult({
        brandId: brandResult.data,
        categoryId: categoryResult.data,
        displayName: displayNameResult.data,
        productTypeId: typeResult.data,
    });
}

export async function listLookupFilterColumnValues(
    context: PlutoWorkspaceClientContext & {
        displayNameColumnId: Uuid | null;
        slugColumnId: Uuid | null;
        templateId: Uuid;
    },
): Promise<Result<{displayName: Array<string>; slug: Array<string>}>> {
    const displayNameResult = await listMappedCatalogueColumnValues({
        columnId: context.displayNameColumnId,
        context: context,
        templateId: context.templateId,
    });
    if (displayNameResult.success !== true) {
        return displayNameResult;
    }
    const slugResult = await listMappedCatalogueColumnValues({
        columnId: context.slugColumnId,
        context: context,
        templateId: context.templateId,
    });
    if (slugResult.success !== true) {
        return slugResult;
    }
    return dataResult({
        displayName: displayNameResult.data,
        slug: slugResult.data,
    });
}

export async function listProductBrandFilterColumnValues(context: PlutoWorkspaceClientContext): Promise<Result<{displayName: Array<string>; slug: Array<string>}>> {
    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }
    return listLookupFilterColumnValues({
        ...context,
        displayNameColumnId: templatesResult.data.brandColumns.displayNameColumnId,
        slugColumnId: templatesResult.data.brandColumns.slugColumnId,
        templateId: templatesResult.data.brandColumns.templateId,
    });
}

export async function listProductCategoryFilterColumnValues(context: PlutoWorkspaceClientContext): Promise<Result<{displayName: Array<string>; slug: Array<string>}>> {
    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }
    return listLookupFilterColumnValues({
        ...context,
        displayNameColumnId: templatesResult.data.categoryColumns.displayNameColumnId,
        slugColumnId: templatesResult.data.categoryColumns.slugColumnId,
        templateId: templatesResult.data.categoryColumns.templateId,
    });
}

export async function listProductTypeFilterColumnValues(context: PlutoWorkspaceClientContext): Promise<Result<{
    categoryId: Array<string>;
    displayName: Array<string>;
    slug: Array<string>;
}>> {
    const templatesResult = await loadTuffexProductTypeCatalogue(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }
    const columns = templatesResult.data.productTypeColumns;
    const textResult = await listLookupFilterColumnValues({
        ...context,
        displayNameColumnId: columns.displayNameColumnId,
        slugColumnId: columns.slugColumnId,
        templateId: columns.templateId,
    });
    if (textResult.success !== true) {
        return textResult;
    }
    const categoryResult = await listMappedCatalogueColumnValues({
        columnId: columns.categoryIdColumnId,
        context: context,
        templateId: columns.templateId,
    });
    if (categoryResult.success !== true) {
        return categoryResult;
    }
    return dataResult({
        categoryId: categoryResult.data,
        displayName: textResult.data.displayName,
        slug: textResult.data.slug,
    });
}

/**
 * Columns needed to group products into categories / part types and to render a SKU row.
 * Everything else on a catalogue row (description, attribute values, additional details, OEM and
 * fitment links) is intentionally left out: it is the bulk of the payload and unused here.
 */
function catalogueColumnIdsForProductListing(columnMapping: TuffexProductColumnMapping): Array<Uuid> {
    return [
        columnMapping.brandIdColumnId,
        columnMapping.categoryIdColumnId,
        columnMapping.displayNameColumnId,
        columnMapping.imageIdsColumnId,
        columnMapping.priceColumnId,
        columnMapping.productTypeIdColumnId,
        columnMapping.skuColumnId,
        columnMapping.slugColumnId,
    ].filter((columnId): columnId is Uuid => columnId !== null);
}

export async function listAdminProductsWithCatalogueVariants(
    context: PlutoWorkspaceClientContext,
): Promise<Result<Array<ProductWithVariants>>> {
    const columnMappingResult = await loadTuffexProductColumnMapping(context);
    if (columnMappingResult.success !== true) {
        return columnMappingResult;
    }

    const cataloguesResult = await fetchSlimCatalogueRowsForTemplate({
        ...context,
        columnIds: catalogueColumnIdsForProductListing(columnMappingResult.data),
        includeArchived: true,
        productCatalogueTemplateId: columnMappingResult.data.templateId,
    });
    if (cataloguesResult.success !== true) {
        return cataloguesResult;
    }

    const childrenByParentId = new Map<Uuid, Array<TuffexProductCatalogueRow>>();
    for (const catalogue of cataloguesResult.data) {
        if (catalogue.parentId === null) {
            continue;
        }
        const existing = childrenByParentId.get(catalogue.parentId);
        if (existing === undefined) {
            childrenByParentId.set(catalogue.parentId, [catalogue]);
        } else {
            existing.push(catalogue);
        }
    }

    return dataResult(
        cataloguesResult.data
            .filter((plutoProductCatalogue) => {
                return plutoProductCatalogue.parentId === null;
            })
            .map((plutoProductCatalogue) => {
                return mapPlutoProductCatalogueToProductWithVariants({
                    childCatalogues: childrenByParentId.get(plutoProductCatalogue.id) ?? [],
                    columnMapping: columnMappingResult.data,
                    plutoProductCatalogue: plutoProductCatalogue,
                });
            }),
    );
}

export async function listProductsWithVariants({
    intellsysBaseUrl,
    accessToken,
    listParams,
}: {
    intellsysBaseUrl: string;
    accessToken: string;
    listParams?: ListProductsRequest;
}): Promise<Result<Array<ProductWithVariants>>> {
    const productsResult = await robustJsonResultClientFetch<Array<ProductWithVariants>>(
        `${intellsysBaseUrl}/sub-system/pluto/api/admin/product/list-products-with-variants`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(listParams ?? {}),
        },
        zod.array(productWithVariantsZodValidator),
    );
    return productsResult;
}

export async function getProductById({
    accessToken,
    intellsysBaseUrl,
    productId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    productId: string;
}): Promise<Result<ProductWithVariants>> {
    const columnMappingResult = await loadTuffexProductColumnMapping({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (columnMappingResult.success !== true) {
        return columnMappingResult;
    }

    const cataloguesResult = await fetchTuffexProductCatalogues({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (cataloguesResult.success !== true) {
        return cataloguesResult;
    }

    const productCatalogue = cataloguesResult.data.find((catalogue) => {
        return catalogue.id === productId;
    });
    if (productCatalogue === undefined) {
        return errorResult(new Error(`Product catalogue not found: ${productId}`));
    }

    const childCatalogues = cataloguesResult.data.filter((catalogue) => {
        return catalogue.parentId === productId;
    });

    const inventoryCountResult = await loadInventoryCountByProductCatalogueId({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    const inventoryCountByProductCatalogueId =
        inventoryCountResult.success === true ? inventoryCountResult.data : new Map<Uuid, number>();

    const oemPartByIdResult = await buildOemPartByIdMap({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (oemPartByIdResult.success !== true) {
        return oemPartByIdResult;
    }

    const machineByIdResult = await buildMachineByIdMap({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (machineByIdResult.success !== true) {
        return machineByIdResult;
    }

    const compatibilityLinksByVariantIdResult = await buildCompatibilityLinksByVariantIdMap({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (compatibilityLinksByVariantIdResult.success !== true) {
        return compatibilityLinksByVariantIdResult;
    }

    const productWithVariants = mapPlutoProductCatalogueToProductWithVariants({
        childCatalogues: childCatalogues,
        columnMapping: columnMappingResult.data,
        compatibilityLinksByVariantId: compatibilityLinksByVariantIdResult.data,
        inventoryCountByProductCatalogueId: inventoryCountByProductCatalogueId,
        machineById: machineByIdResult.data,
        oemPartById: oemPartByIdResult.data,
        plutoProductCatalogue: productCatalogue,
    });

    return dataResult(
        await hydrateProductWithVariantsMediaImages({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            productWithVariants: productWithVariants,
            workspaceId: workspaceId,
        }),
    );
}

async function hydrateProductWithVariantsMediaImages({
    accessToken,
    intellsysBaseUrl,
    productWithVariants,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    productWithVariants: ProductWithVariants;
}): Promise<ProductWithVariants> {
    const allImageIds = new Set<string>();
    for (const imageId of productWithVariants.imageIds) {
        allImageIds.add(imageId);
    }
    for (const variant of productWithVariants.variants) {
        for (const imageId of variant.imageIds) {
            allImageIds.add(imageId);
        }
    }

    if (allImageIds.size === 0) {
        return productWithVariants;
    }

    const resolvedImages = await resolveImageIdsFromApi({
        accessToken: accessToken,
        imageIds: [...allImageIds] as Array<Uuid>,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    const resolvedImageById = new Map(resolvedImages.map((image) => [image.id, image]));

    const pickOrderedImages = (imageIds: Array<Uuid>): Array<ResolvedImage> => {
        const images: Array<ResolvedImage> = [];
        for (const imageId of imageIds) {
            const image = resolvedImageById.get(imageId);
            if (image !== undefined) {
                images.push(image);
            }
        }
        return images;
    };

    return {
        ...productWithVariants,
        images: pickOrderedImages(productWithVariants.imageIds),
        variants: productWithVariants.variants.map((variant) => {
            return {
                ...variant,
                images: pickOrderedImages(variant.imageIds),
            };
        }),
    };
}

export async function createProduct({
    accessToken,
    intellsysBaseUrl,
    product,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    product: PostProduct;
}): Promise<Result<Uuid>> {
    const columnMappingResult = await loadTuffexProductColumnMapping({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (columnMappingResult.success !== true) {
        return columnMappingResult;
    }

    const slug = product.slug.trim();
    if (slug.length === 0) {
        return errorResult(new Error("Slug is required"));
    }

    const createResult = await createCatalogueRow({
        accessToken: accessToken,
        additionalColumnValues: buildProductAdditionalColumnValues({
            brandId: product.brandId,
            categoryId: product.categoryId ?? null,
            columnMapping: columnMappingResult.data,
            description: product.description,
            displayName: product.displayName,
            imageIds: product.imageIds,
            includeSellableFields: false,
            price: 0,
            productTypeId: product.type,
            sku: "",
            slug: slug,
            variantAttributeIds: product.variantAttributeIds,
        }),
        parentId: null,
        intellsysBaseUrl: intellsysBaseUrl,
        productCatalogueTemplateId: columnMappingResult.data.templateId,
        status: catalogueStatusFromIsPublic(product.isPublic),
        workspaceId: workspaceId,
    });
    if (createResult.success !== true) {
        return createResult;
    }

    return dataResult(createResult.data);
}

export async function updateProductById({
    accessToken,
    intellsysBaseUrl,
    product,
    productId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    product: PostProduct;
    productId: string;
}): Promise<Result<string>> {
    const [columnMappingResult, productCatalogueResult] = await Promise.all([
        loadTuffexProductColumnMapping({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            workspaceId: workspaceId,
        }),
        fetchTuffexProductCatalogueById({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            productCatalogueId: productId,
            workspaceId: workspaceId,
        }),
    ]);
    if (columnMappingResult.success !== true) {
        return columnMappingResult;
    }
    if (productCatalogueResult.success !== true) {
        return productCatalogueResult;
    }

    const productCatalogue = productCatalogueResult.data;
    const slug = product.slug.trim().length > 0 ? product.slug.trim() : product.displayName.trim();
    const sku = slug;
    const sellingPrice = extractCataloguePrice(productCatalogue, columnMappingResult.data);

    const updateResult = await updateTuffexProductCatalogueRow({
        accessToken: accessToken,
        brandId: product.brandId,
        categoryId: product.categoryId ?? null,
        columnMapping: columnMappingResult.data,
        description: product.description,
        displayName: product.displayName,
        imageIds: product.imageIds,
        isPublic: product.isPublic,
        parentId: productCatalogue.parentId,
        intellsysBaseUrl: intellsysBaseUrl,
        plutoProductCatalogue: productCatalogue,
        productTypeId: product.type,
        sellingPrice: sellingPrice,
        sku: sku,
        slug: slug,
        variantAttributeIds: product.variantAttributeIds,
        workspaceId: workspaceId,
    });
    if (updateResult.success !== true) {
        return updateResult;
    }

    return dataResult(productId);
}

export async function deleteProductById({
    accessToken,
    intellsysBaseUrl,
    productId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    productId: string;
}): Promise<Result<string>> {
    const parsedProductId = uuidZodValidator.safeParse(productId);
    if (parsedProductId.success !== true) {
        return errorResult(new Error(`Invalid product id: ${productId}`));
    }

    const [columnMappingResult, productCatalogueResult] = await Promise.all([
        loadTuffexProductColumnMapping({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            workspaceId: workspaceId,
        }),
        fetchTuffexProductCatalogueById({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            productCatalogueId: parsedProductId.data,
            workspaceId: workspaceId,
        }),
    ]);
    if (columnMappingResult.success !== true) {
        return columnMappingResult;
    }
    if (productCatalogueResult.success !== true) {
        return productCatalogueResult;
    }

    const productCatalogue = productCatalogueResult.data;

    const deleteResult = await deleteCatalogueRow({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        catalogueRow: {
            ...productCatalogue,
            templateId: productCatalogue.templateId ?? columnMappingResult.data.templateId,
        },
    });
    if (deleteResult.success !== true) {
        return deleteResult;
    }

    return dataResult(productId);
}

export async function loadOttokartVariantsForDisplay({
    accessToken,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext): Promise<Array<Variant>> {
    const variantsResult = await listOttokartVariantsFromPluto({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (variantsResult.success !== true) {
        return [];
    }
    return variantsResult.data;
}

export async function listVariants({
    accessToken,
    listParams: _listParams,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    listParams?: ListProductsRequest;
}): Promise<Result<Array<Variant>>> {
    return listOttokartVariantsFromPluto({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
}

export async function createVariant({
    accessToken,
    intellsysBaseUrl,
    variant,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    variant: PostVariant;
}): Promise<Result<Uuid>> {
    const columnMappingResult = await loadTuffexProductColumnMapping({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (columnMappingResult.success !== true) {
        return columnMappingResult;
    }

    const sku = variant.sku.trim();
    if (sku.length === 0) {
        return errorResult(new Error("Variant SKU is required."));
    }

    const displayName = variant.displayName.trim().length > 0 ? variant.displayName.trim() : sku;
    const createResult = await createCatalogueRow({
        accessToken: accessToken,
        additionalColumnValues: buildProductAdditionalColumnValues({
            additionalDetails: variant.additionalDetails,
            attributeValues: variant.attributeValues,
            brandId: null,
            categoryId: null,
            columnMapping: columnMappingResult.data,
            description: variant.description,
            displayName: displayName,
            equivalentOemPartIds: variant.equivalentOemPartIds,
            imageIds: variant.imageIds,
            price: variant.sellingPrice,
            sku: sku,
            slug: variant.slug.trim().length > 0 ? variant.slug.trim() : sku,
        }),
        parentId: variant.productId,
        intellsysBaseUrl: intellsysBaseUrl,
        productCatalogueTemplateId: columnMappingResult.data.templateId,
        status: catalogueStatusFromIsPublic(variant.isPublic),
        workspaceId: workspaceId,
    });
    if (createResult.success !== true) {
        return createResult;
    }

    const createdCatalogueId = createResult.data;

    const inventoryUpsertResult = await upsertProductCatalogueInventory({
        accessToken: accessToken,
        inventory: variant.inventory,
        intellsysBaseUrl: intellsysBaseUrl,
        productCatalogueId: createdCatalogueId,
        workspaceId: workspaceId,
    });
    if (inventoryUpsertResult.success !== true) {
        return inventoryUpsertResult;
    }

    const syncLinksResult = await syncVariantCompatibilityLinks({
        accessToken: accessToken,
        masterNodeIds: variant.compatibleMachineIds,
        intellsysBaseUrl: intellsysBaseUrl,
        variantId: createdCatalogueId,
        workspaceId: workspaceId,
    });
    if (syncLinksResult.success !== true) {
        return syncLinksResult;
    }

    return dataResult(createdCatalogueId);
}

export async function updateVariantById({
    accessToken,
    intellsysBaseUrl,
    skipCompatibilitySync = false,
    skipInventoryUpsert = false,
    variant,
    variantId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    skipCompatibilitySync?: boolean;
    skipInventoryUpsert?: boolean;
    variant: PostVariant;
    variantId: string;
}): Promise<Result<string>> {
    const [columnMappingResult, productCatalogueResult] = await Promise.all([
        loadTuffexProductColumnMapping({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            workspaceId: workspaceId,
        }),
        fetchTuffexProductCatalogueById({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            productCatalogueId: variantId,
            workspaceId: workspaceId,
        }),
    ]);
    if (columnMappingResult.success !== true) {
        return columnMappingResult;
    }
    if (productCatalogueResult.success !== true) {
        return productCatalogueResult;
    }

    const productCatalogue = productCatalogueResult.data;
    const sku = variant.sku.trim().length > 0 ? variant.sku.trim() : extractCatalogueSku(productCatalogue, columnMappingResult.data);
    const displayName = variant.displayName.trim().length > 0 ? variant.displayName.trim() : sku;

    const updateResult = await updateTuffexProductCatalogueRow({
        accessToken: accessToken,
        additionalDetails: variant.additionalDetails,
        attributeValues: variant.attributeValues,
        brandId: null,
        categoryId: null,
        columnMapping: columnMappingResult.data,
        description: variant.description,
        displayName: displayName,
        equivalentOemPartIds: variant.equivalentOemPartIds,
        imageIds: variant.imageIds,
        isPublic: variant.isPublic,
        parentId: productCatalogue.parentId,
        intellsysBaseUrl: intellsysBaseUrl,
        plutoProductCatalogue: productCatalogue,
        sellingPrice: variant.sellingPrice,
        sku: sku,
        slug: variant.slug.trim().length > 0 ? variant.slug.trim() : sku,
        workspaceId: workspaceId,
    });
    if (updateResult.success !== true) {
        return updateResult;
    }

    if (skipInventoryUpsert !== true) {
        const inventoryUpsertResult = await upsertProductCatalogueInventory({
            accessToken: accessToken,
            inventory: variant.inventory,
            intellsysBaseUrl: intellsysBaseUrl,
            productCatalogueId: productCatalogue.id,
            workspaceId: workspaceId,
        });
        if (inventoryUpsertResult.success !== true) {
            return inventoryUpsertResult;
        }
    }

    if (skipCompatibilitySync !== true) {
        const syncLinksResult = await syncVariantCompatibilityLinks({
            accessToken: accessToken,
            masterNodeIds: variant.compatibleMachineIds,
            intellsysBaseUrl: intellsysBaseUrl,
            variantId: productCatalogue.id,
            workspaceId: workspaceId,
        });
        if (syncLinksResult.success !== true) {
            return syncLinksResult;
        }
    }

    return dataResult(variantId);
}

export async function listOttokartVariantsFromPluto({
    accessToken,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext): Promise<Result<Array<Variant>>> {
    const columnMappingResult = await loadTuffexProductColumnMapping({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (columnMappingResult.success !== true) {
        return columnMappingResult;
    }

    const cataloguesResult = await fetchTuffexProductCatalogues({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (cataloguesResult.success !== true) {
        return cataloguesResult;
    }

    const inventoryCountResult = await loadInventoryCountByProductCatalogueId({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    const inventoryCountByProductCatalogueId =
        inventoryCountResult.success === true ? inventoryCountResult.data : new Map<Uuid, number>();

    const oemPartByIdResult = await buildOemPartByIdMap({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (oemPartByIdResult.success !== true) {
        return oemPartByIdResult;
    }

    const machineByIdResult = await buildMachineByIdMap({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (machineByIdResult.success !== true) {
        return machineByIdResult;
    }

    const compatibilityLinksByVariantIdResult = await buildCompatibilityLinksByVariantIdMap({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (compatibilityLinksByVariantIdResult.success !== true) {
        return compatibilityLinksByVariantIdResult;
    }

    const variants = cataloguesResult.data
        .filter((plutoProductCatalogue) => {
            return plutoProductCatalogue.parentId !== null;
        })
        .map((plutoProductCatalogue) => {
            return mapPlutoProductCatalogueToVariant(
                plutoProductCatalogue,
                columnMappingResult.data,
                inventoryCountByProductCatalogueId,
                oemPartByIdResult.data,
                machineByIdResult.data,
                compatibilityLinksByVariantIdResult.data,
            );
        });

    const allImageIds = new Set<string>();
    for (const variant of variants) {
        for (const imageId of variant.imageIds) {
            allImageIds.add(imageId);
        }
    }

    if (allImageIds.size === 0) {
        return dataResult(variants);
    }

    const resolvedImages = await resolveImageIdsFromApi({
        accessToken: accessToken,
        imageIds: [...allImageIds] as Array<Uuid>,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    const resolvedImageById = new Map(resolvedImages.map((image) => [image.id, image]));

    return dataResult(
        variants.map((variant) => {
            const images: Array<ResolvedImage> = [];
            for (const imageId of variant.imageIds) {
                const image = resolvedImageById.get(imageId);
                if (image !== undefined) {
                    images.push(image);
                }
            }
            return {
                ...variant,
                images: images,
            };
        }),
    );
}

export async function getProductVariants({
    accessToken,
    intellsysBaseUrl,
    productId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    productId: string;
}): Promise<Result<Array<Variant>>> {
    const productResult = await getProductById({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        productId: productId,
        workspaceId: workspaceId,
    });
    if (productResult.success !== true) {
        return productResult;
    }
    return dataResult(productResult.data.variants);
}

export async function deleteVariantById({
    accessToken,
    intellsysBaseUrl,
    variantId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    variantId: string;
}): Promise<Result<string>> {
    return deleteProductById({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        productId: variantId,
        workspaceId: workspaceId,
    });
}

export async function listProductTypes(
    context: PlutoWorkspaceClientContext & {
        filters?: CatalogueTextColumnFilters;
        limit?: number;
        offset?: number;
        search?: string;
    },
): Promise<Result<Array<ProductType>>> {
    const templatesResult = await loadTuffexProductTypeCatalogue(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        ...context,
        namedFilters: namedFiltersFromTextColumns({
            displayNameColumnId: templatesResult.data.productTypeColumns.displayNameColumnId,
            filters: catalogueFiltersWithIds({
                columnIds: [
                    {columnId: templatesResult.data.productTypeColumns.categoryIdColumnId, values: context.filters?.categoryIds},
                ],
                filters: context.filters,
            }),
            slugColumnId: templatesResult.data.productTypeColumns.slugColumnId,
        }),
        productCatalogueTemplateId: templatesResult.data.productTypeColumns.templateId,
        search: context.search,
        searchColumnIds: [templatesResult.data.productTypeColumns.displayNameColumnId, templatesResult.data.productTypeColumns.slugColumnId].filter((columnId): columnId is Uuid => columnId !== null),
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    return dataResult(
        rowsResult.data.map((row) => {
            return mapLookupCatalogueRowToProductType(row, templatesResult.data.productTypeColumns);
        }),
    );
}

export async function countProductTypes(context: PlutoWorkspaceClientContext & {filters?: CatalogueTextColumnFilters; search?: string}): Promise<Result<number>> {
    const templatesResult = await loadTuffexProductTypeCatalogue(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }
    return countCatalogueRowsForTemplate({
        ...context,
        namedFilters: namedFiltersFromTextColumns({
            displayNameColumnId: templatesResult.data.productTypeColumns.displayNameColumnId,
            filters: catalogueFiltersWithIds({
                columnIds: [
                    {columnId: templatesResult.data.productTypeColumns.categoryIdColumnId, values: context.filters?.categoryIds},
                ],
                filters: context.filters,
            }),
            slugColumnId: templatesResult.data.productTypeColumns.slugColumnId,
        }),
        productCatalogueTemplateId: templatesResult.data.productTypeColumns.templateId,
        search: context.search,
        searchColumnIds: [templatesResult.data.productTypeColumns.displayNameColumnId, templatesResult.data.productTypeColumns.slugColumnId].filter((columnId): columnId is Uuid => columnId !== null),
    });
}

export async function addProductType({
    productType,
    ...context
}: PlutoWorkspaceClientContext & {
    productType: PostProductType;
}): Promise<Result<Uuid>> {
    const templatesResult = await loadTuffexProductTypeCatalogue(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const columnMapping = templatesResult.data.productTypeColumns;
    const slug = productType.slug.trim();
    if (slug.length === 0) {
        return errorResult(new Error("Product type slug is required."));
    }

    const createResult = await createCatalogueRow({
        ...context,
        additionalColumnValues: buildProductTypeCatalogueAdditionalColumnValues({
            categoryId: productType.categoryId ?? null,
            columnMapping: columnMapping,
            displayName: productType.displayName,
            slug: slug,
            sortOrder: productType.sortOrder,
        }),
        parentId: null,
        productCatalogueTemplateId: columnMapping.templateId,
    });
    if (createResult.success !== true) {
        return createResult;
    }

    const createdRow = await findCatalogueRowBySlug({
        ...context,
        columnMapping: columnMapping,
        productCatalogueTemplateId: columnMapping.templateId,
        slug: slug,
    });
    if (createdRow === null) {
        return errorResult(new Error(`Created product type catalogue row could not be found for slug: ${slug}`));
    }

    return dataResult(createdRow.id);
}

function mapLookupCatalogueRowToProductCategory(
    catalogueRow: TuffexProductCatalogueRow,
    columnMapping: TuffexLookupColumnMapping,
): ProductCategory {
    const fields = mapLookupCatalogueRowToDisplayFields({
        catalogueRow: catalogueRow,
        columnMapping: columnMapping,
    });

    return {
        id: catalogueRow.id,
        createdAt: catalogueRow.createdAt,
        updatedAt: catalogueRow.updatedAt,
        createdBy: catalogueRow.createdBy,
        updatedBy: catalogueRow.updatedBy,
        displayName: fields.displayName,
        slug: fields.slug,
        sortOrder: fields.sortOrder,
        imageId: fields.imageId,
        parentId: catalogueRow.parentId,
        metaTitle: null,
        metaDescription: null,
    };
}

function mapLookupCatalogueRowToProductBrand(
    catalogueRow: TuffexProductCatalogueRow,
    columnMapping: TuffexLookupColumnMapping,
): ProductBrand {
    const fields = mapLookupCatalogueRowToDisplayFields({
        catalogueRow: catalogueRow,
        columnMapping: columnMapping,
    });

    return {
        id: catalogueRow.id,
        createdAt: catalogueRow.createdAt,
        updatedAt: catalogueRow.updatedAt,
        createdBy: catalogueRow.createdBy,
        updatedBy: catalogueRow.updatedBy,
        displayName: fields.displayName,
        slug: fields.slug,
        sortOrder: fields.sortOrder,
        imageId: fields.imageId,
    };
}

function mediaLibraryFileNameStem(fileName: string): string {
    const trimmed = fileName.trim().toLowerCase();
    const lastDot = trimmed.lastIndexOf(".");
    if (lastDot <= 0) {
        return trimmed;
    }
    return trimmed.slice(0, lastDot);
}

function mediaLibraryFileMatchesBrand({
    displayName,
    fileName,
    slug,
}: {
    displayName: string;
    fileName: string;
    slug: string;
}): boolean {
    const stem = mediaLibraryFileNameStem(fileName);
    if (stem.length === 0) {
        return false;
    }

    const keys = [slug.trim().toLowerCase(), displayName.trim().toLowerCase().replace(/\s+/g, "-")].filter((key) => {
        return key.length > 0;
    });

    for (const key of keys) {
        if (stem === key || stem === `${key}-logo` || stem.startsWith(`${key}-logo`) || stem.startsWith(`${key}-`)) {
            return true;
        }
    }

    return false;
}

async function listBrandLogoMediaLibraryFiles(context: PlutoWorkspaceClientContext): Promise<Array<{id: Uuid; name: string}>> {
    const folderNames = [WEBSITE_BRANDS_MEDIA_LIBRARY_FOLDER_NAME, PRODUCT_BRANDS_MEDIA_LIBRARY_FOLDER_NAME];
    const files: Array<{id: Uuid; name: string}> = [];

    for (const folderName of folderNames) {
        const folderId = await findExistingMediaLibraryFolderId({
            accessToken: context.accessToken,
            folderName: folderName,
            intellsysBaseUrl: context.intellsysBaseUrl,
            workspaceId: context.workspaceId,
        });
        if (folderId === null) {
            continue;
        }

        const dirents = await listMediaLibraryFolderFileDirents({
            accessToken: context.accessToken,
            folderId: folderId,
            intellsysBaseUrl: context.intellsysBaseUrl,
            workspaceId: context.workspaceId,
        });
        for (const dirent of dirents) {
            files.push({
                id: dirent.id,
                name: dirent.name,
            });
        }
    }

    return files;
}

async function attachBrandImagesFromMediaLibrary({
    brands,
    context,
}: {
    brands: Array<ProductBrand>;
    context: PlutoWorkspaceClientContext;
}): Promise<Array<ProductBrand>> {
    const brandsMissingImages = brands.filter((brand) => {
        return brand.imageId === null;
    });
    if (brandsMissingImages.length === 0) {
        return brands;
    }

    const files = await listBrandLogoMediaLibraryFiles(context);
    if (files.length === 0) {
        return brands;
    }

    return brands.map((brand) => {
        if (brand.imageId !== null) {
            return brand;
        }

        const matchedFile = files.find((file) => {
            return mediaLibraryFileMatchesBrand({
                displayName: brand.displayName,
                fileName: file.name,
                slug: brand.slug,
            });
        });
        if (matchedFile === undefined) {
            return brand;
        }

        return {
            ...brand,
            imageId: matchedFile.id,
        };
    });
}

function mapLookupCatalogueRowToProductType(
    catalogueRow: TuffexProductCatalogueRow,
    columnMapping: TuffexProductTypeColumnMapping,
): ProductType {
    const fields = mapLookupCatalogueRowToDisplayFields({
        catalogueRow: catalogueRow,
        columnMapping: {
            displayNameColumnId: columnMapping.displayNameColumnId,
            imageIdColumnId: null,
            slugColumnId: columnMapping.slugColumnId,
            sortOrderColumnId: columnMapping.sortOrderColumnId,
            templateId: columnMapping.templateId,
        },
    });

    return {
        id: catalogueRow.id,
        createdAt: catalogueRow.createdAt,
        updatedAt: catalogueRow.updatedAt,
        createdBy: catalogueRow.createdBy,
        updatedBy: catalogueRow.updatedBy,
        displayName: fields.displayName,
        slug: fields.slug,
        sortOrder: fields.sortOrder,
        categoryId: readCatalogueUuidValue(catalogueRow.additionalColumnValues, columnMapping.categoryIdColumnId),
    };
}

export async function listProductBrands(
    context: PlutoWorkspaceClientContext & {
        /**
         * When true, brands with no catalogue `imageId` are matched against media-library
         * logo folders (`brands` / `product-brands`). That walks seed-root + child dirents —
         * only enable on brand-admin UIs that need the fallback. Default: false.
         */
        attachMissingImagesFromMediaLibrary?: boolean;
        filters?: CatalogueTextColumnFilters;
        limit?: number;
        offset?: number;
        search?: string;
    },
): Promise<Result<Array<ProductBrand>>> {
    const attachMissingImagesFromMediaLibrary = context.attachMissingImagesFromMediaLibrary === true;

    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        ...context,
        namedFilters: namedFiltersFromTextColumns({
            displayNameColumnId: templatesResult.data.brandColumns.displayNameColumnId,
            filters: context.filters,
            slugColumnId: templatesResult.data.brandColumns.slugColumnId,
        }),
        productCatalogueTemplateId: templatesResult.data.brandColumns.templateId,
        search: context.search,
        searchColumnIds: [templatesResult.data.brandColumns.displayNameColumnId, templatesResult.data.brandColumns.slugColumnId].filter((columnId): columnId is Uuid => columnId !== null),
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    const brands = rowsResult.data.map((row) => {
        return mapLookupCatalogueRowToProductBrand(row, templatesResult.data.brandColumns);
    });

    if (attachMissingImagesFromMediaLibrary !== true) {
        return dataResult(brands);
    }

    return dataResult(await attachBrandImagesFromMediaLibrary({brands: brands, context: context}));
}

export async function countProductBrands(context: PlutoWorkspaceClientContext & {filters?: CatalogueTextColumnFilters; search?: string}): Promise<Result<number>> {
    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }
    return countCatalogueRowsForTemplate({
        ...context,
        namedFilters: namedFiltersFromTextColumns({
            displayNameColumnId: templatesResult.data.brandColumns.displayNameColumnId,
            filters: context.filters,
            slugColumnId: templatesResult.data.brandColumns.slugColumnId,
        }),
        productCatalogueTemplateId: templatesResult.data.brandColumns.templateId,
        search: context.search,
        searchColumnIds: [templatesResult.data.brandColumns.displayNameColumnId, templatesResult.data.brandColumns.slugColumnId].filter((columnId): columnId is Uuid => columnId !== null),
    });
}

export async function addProductBrand({
    brand,
    ...context
}: PlutoWorkspaceClientContext & {
    brand: PostProductBrand;
}): Promise<Result<Uuid>> {
    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const columnMapping = templatesResult.data.brandColumns;
    const slug = brand.slug.trim();
    if (slug.length === 0) {
        return errorResult(new Error("Brand slug is required."));
    }

    const createResult = await createCatalogueRow({
        ...context,
        additionalColumnValues: buildLookupCatalogueAdditionalColumnValues({
            columnMapping: columnMapping,
            displayName: brand.displayName,
            imageId: brand.imageId ?? null,
            slug: slug,
            sortOrder: brand.sortOrder,
        }),
        parentId: null,
        productCatalogueTemplateId: columnMapping.templateId,
    });
    if (createResult.success !== true) {
        return createResult;
    }

    const createdRow = await findCatalogueRowBySlug({
        ...context,
        columnMapping: columnMapping,
        productCatalogueTemplateId: columnMapping.templateId,
        slug: slug,
    });
    if (createdRow === null) {
        return errorResult(new Error(`Created brand catalogue row could not be found for slug: ${slug}`));
    }

    return dataResult(createdRow.id);
}

export async function updateProductBrand({
    brand,
    ...context
}: PlutoWorkspaceClientContext & {
    brand: PostProductBrand;
}): Promise<Result<string>> {
    if (brand.id === undefined) {
        return errorResult(new Error("Brand id is required for update."));
    }

    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        ...context,
        productCatalogueTemplateId: templatesResult.data.brandColumns.templateId,
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    const catalogueRow = rowsResult.data.find((row) => {
        return row.id === brand.id;
    });
    if (catalogueRow === undefined) {
        return errorResult(new Error(`Brand catalogue row not found: ${brand.id}`));
    }

    const columnMapping = templatesResult.data.brandColumns;
    const updateResult = await updateCatalogueRow({
        ...context,
        additionalColumnValues: buildLookupCatalogueAdditionalColumnValues({
            columnMapping: columnMapping,
            displayName: brand.displayName,
            imageId: brand.imageId ?? null,
            slug: brand.slug,
            sortOrder: brand.sortOrder,
        }),
        catalogueRow: catalogueRow,
        parentId: null,
    });
    if (updateResult.success !== true) {
        return updateResult;
    }

    return dataResult(brand.id);
}

export async function deleteProductBrand({
    brandId,
    ...context
}: PlutoWorkspaceClientContext & {
    brandId: string;
}): Promise<Result<string>> {
    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        ...context,
        productCatalogueTemplateId: templatesResult.data.brandColumns.templateId,
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    const catalogueRow = rowsResult.data.find((row) => {
        return row.id === brandId;
    });
    if (catalogueRow === undefined) {
        return errorResult(new Error(`Brand catalogue row not found: ${brandId}`));
    }

    const deleteResult = await deleteCatalogueRow({
        ...context,
        catalogueRow: catalogueRow,
    });
    if (deleteResult.success !== true) {
        return deleteResult;
    }

    return dataResult(brandId);
}

export async function updateProductType({
    productType,
    ...context
}: PlutoWorkspaceClientContext & {
    productType: PostProductType;
}): Promise<Result<string>> {
    if (productType.id === undefined) {
        return errorResult(new Error("Product type id is required for update."));
    }

    const templatesResult = await loadTuffexProductTypeCatalogue(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        ...context,
        productCatalogueTemplateId: templatesResult.data.productTypeColumns.templateId,
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    const catalogueRow = rowsResult.data.find((row) => {
        return row.id === productType.id;
    });
    if (catalogueRow === undefined) {
        return errorResult(new Error(`Product type catalogue row not found: ${productType.id}`));
    }

    const columnMapping = templatesResult.data.productTypeColumns;
    const updateResult = await updateCatalogueRow({
        ...context,
        additionalColumnValues: buildProductTypeCatalogueAdditionalColumnValues({
            categoryId: productType.categoryId ?? null,
            columnMapping: columnMapping,
            displayName: productType.displayName,
            slug: productType.slug,
            sortOrder: productType.sortOrder,
        }),
        catalogueRow: catalogueRow,
        parentId: null,
    });
    if (updateResult.success !== true) {
        return updateResult;
    }

    return dataResult(productType.id);
}

export async function deleteProductType({
    productTypeId,
    ...context
}: PlutoWorkspaceClientContext & {
    productTypeId: string;
}): Promise<Result<string>> {
    const templatesResult = await loadTuffexProductTypeCatalogue(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        ...context,
        productCatalogueTemplateId: templatesResult.data.productTypeColumns.templateId,
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    const catalogueRow = rowsResult.data.find((row) => {
        return row.id === productTypeId;
    });
    if (catalogueRow === undefined) {
        return errorResult(new Error(`Product type catalogue row not found: ${productTypeId}`));
    }

    const deleteResult = await deleteCatalogueRow({
        ...context,
        catalogueRow: catalogueRow,
    });
    if (deleteResult.success !== true) {
        return deleteResult;
    }

    return dataResult(productTypeId);
}

export async function listProductCategories(
    context: PlutoWorkspaceClientContext & {
        filters?: CatalogueTextColumnFilters;
        limit?: number;
        offset?: number;
        search?: string;
    },
): Promise<Result<Array<ProductCategory>>> {
    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        ...context,
        namedFilters: namedFiltersFromTextColumns({
            displayNameColumnId: templatesResult.data.categoryColumns.displayNameColumnId,
            filters: context.filters,
            slugColumnId: templatesResult.data.categoryColumns.slugColumnId,
        }),
        productCatalogueTemplateId: templatesResult.data.categoryColumns.templateId,
        search: context.search,
        searchColumnIds: [templatesResult.data.categoryColumns.displayNameColumnId, templatesResult.data.categoryColumns.slugColumnId].filter((columnId): columnId is Uuid => columnId !== null),
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    return dataResult(
        rowsResult.data.map((row) => {
            return mapLookupCatalogueRowToProductCategory(row, templatesResult.data.categoryColumns);
        }),
    );
}

export async function countProductCategories(context: PlutoWorkspaceClientContext & {filters?: CatalogueTextColumnFilters; search?: string}): Promise<Result<number>> {
    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }
    return countCatalogueRowsForTemplate({
        ...context,
        namedFilters: namedFiltersFromTextColumns({
            displayNameColumnId: templatesResult.data.categoryColumns.displayNameColumnId,
            filters: context.filters,
            slugColumnId: templatesResult.data.categoryColumns.slugColumnId,
        }),
        productCatalogueTemplateId: templatesResult.data.categoryColumns.templateId,
        search: context.search,
        searchColumnIds: [templatesResult.data.categoryColumns.displayNameColumnId, templatesResult.data.categoryColumns.slugColumnId].filter((columnId): columnId is Uuid => columnId !== null),
    });
}

export async function addProductCategory({
    category,
    ...context
}: PlutoWorkspaceClientContext & {
    category: PostProductCategory;
}): Promise<Result<Uuid>> {
    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const columnMapping = templatesResult.data.categoryColumns;
    const slug = category.slug.trim();
    if (slug.length === 0) {
        return errorResult(new Error("Category slug is required."));
    }

    const createResult = await createCatalogueRow({
        ...context,
        additionalColumnValues: buildLookupCatalogueAdditionalColumnValues({
            columnMapping: columnMapping,
            displayName: category.displayName,
            imageId: category.imageId ?? null,
            slug: slug,
            sortOrder: category.sortOrder,
        }),
        parentId: category.parentId ?? null,
        productCatalogueTemplateId: columnMapping.templateId,
    });
    if (createResult.success !== true) {
        return createResult;
    }

    const createdRow = await findCatalogueRowBySlug({
        ...context,
        columnMapping: columnMapping,
        productCatalogueTemplateId: columnMapping.templateId,
        slug: slug,
    });
    if (createdRow === null) {
        return errorResult(new Error(`Created category catalogue row could not be found for slug: ${slug}`));
    }

    return dataResult(createdRow.id);
}

export async function updateProductCategory({
    category,
    ...context
}: PlutoWorkspaceClientContext & {
    category: PostProductCategory;
}): Promise<Result<string>> {
    if (category.id === undefined) {
        return errorResult(new Error("Category id is required for update."));
    }

    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        ...context,
        productCatalogueTemplateId: templatesResult.data.categoryColumns.templateId,
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    const catalogueRow = rowsResult.data.find((row) => {
        return row.id === category.id;
    });
    if (catalogueRow === undefined) {
        return errorResult(new Error(`Category catalogue row not found: ${category.id}`));
    }

    const columnMapping = templatesResult.data.categoryColumns;
    const updateResult = await updateCatalogueRow({
        ...context,
        additionalColumnValues: buildLookupCatalogueAdditionalColumnValues({
            columnMapping: columnMapping,
            displayName: category.displayName,
            imageId: category.imageId ?? null,
            slug: category.slug,
            sortOrder: category.sortOrder,
        }),
        catalogueRow: catalogueRow,
        parentId: category.parentId ?? null,
    });
    if (updateResult.success !== true) {
        return updateResult;
    }

    return dataResult(category.id);
}

export async function deleteProductCategory({
    categoryId,
    ...context
}: PlutoWorkspaceClientContext & {
    categoryId: string;
}): Promise<Result<string>> {
    const templatesResult = await loadTuffexCatalogueTemplates(context);
    if (templatesResult.success !== true) {
        return templatesResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        ...context,
        productCatalogueTemplateId: templatesResult.data.categoryColumns.templateId,
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    const catalogueRow = rowsResult.data.find((row) => {
        return row.id === categoryId;
    });
    if (catalogueRow === undefined) {
        return errorResult(new Error(`Category catalogue row not found: ${categoryId}`));
    }

    const deleteResult = await deleteCatalogueRow({
        ...context,
        catalogueRow: catalogueRow,
    });
    if (deleteResult.success !== true) {
        return deleteResult;
    }

    return dataResult(categoryId);
}

export async function listVariantAttributes({
    accessToken,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext): Promise<Result<Array<VariantAttributeType>>> {
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/api/admin/ottokart-product/list-variant-attributes",
        body: {},
        responseValidator: zod.array(tuffexVariantAttributeTypeValidator),
    });
}

export async function addVariantAttribute({
    accessToken,
    intellsysBaseUrl,
    variantAttribute,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    variantAttribute: PostVariantAttributeType;
}): Promise<Result<Uuid>> {
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/api/admin/ottokart-product/add-variant-attribute",
        body: variantAttribute,
        responseValidator: uuidZodValidator,
    });
}
